'use client';
import { type ChangeEvent, useMemo } from 'react';

import {
  FieldGroup,
  type FieldGroupType,
} from '@/app/components/forms/field-group';
import {
  OptionsGroup,
  type OptionsGroupOrientation,
} from '@/app/components/forms/options-group';
import {
  controlLabelStyled,
  controlRadioStyled,
  type CustomFormStyleConfig,
  getCustomFormStyle,
  inputBoxStyled,
} from '@/app/components/forms/styles';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA, type DataGAProps } from '@/app/utils/createDataGA';
import { safeCall } from '@/app/utils/safeCall';

export interface RadioOption {
  label?: string;
  value: string;
  checked?: boolean;
  disabled?: boolean;
}

export interface RadioProps extends Omit<FieldGroupType, 'showMaxLength'> {
  /**
   * (Optional) The class name of the component.
   */
  className?: string;

  /**
   * (Optional) The class name of the option group component.
   */
  groupClassName?: string;

  /**
   * The name of the input field.
   */
  name: string;

  /**
   * The options to be rendered as radio buttons.
   */
  options: RadioOption[];

  /**
   * (Optional) The value of the selected option.
   */
  value?: string;

  /**
   * (Optional) The orientation of the radio buttons.
   */
  orientation?: OptionsGroupOrientation;

  /**
   * (Optional) The function to be called when the value changes.
   */
  onChange?: (value: string, option: RadioOption, index: number) => void;

  /**
   * (Optional) Whether the component is in an error state.
   */
  error?: boolean;

  /**
   * (Optional) Whether the component is disabled.
   */
  disabled?: boolean;

  /**
   * (Optional) Custom styles for the component.
   */
  customStyle?: CustomFormStyleConfig;

  /**
   * (Optional) The tracking data for the component.
   */
  tracking?: Omit<DataGAProps, 'click'>;
}

export const Radio = ({
  className,
  groupClassName,
  orientation = 'horizontal',
  theme = 'light',
  name,
  options = [],
  value,
  error = false,
  disabled = false,
  customStyle,
  label,
  ref,
  tracking,
  onChange,
  ...props
}: RadioProps) => {
  const handleChange =
    (option: RadioOption, index: number) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      safeCall(onChange, event.target.value, option, index);
    };

  const renderOptions = useMemo(() => {
    return options.map((option, index) => {
      const isDisabled = !!(disabled || option.disabled);
      const isChecked = option.checked ?? value === option.value;
      const id = `${name}-${option.value}`;
      const isCheckedDisabled = isChecked && isDisabled;

      const labelClass = cn(
        controlLabelStyled({
          theme,
          error,
          disabled: isDisabled,
          checked: isChecked,
          checkedDisabled: isCheckedDisabled,
        }),
        'relative inline-flex cursor-pointer gap-x-2',
        {
          'py-3': option.label,
        },
        getCustomFormStyle(customStyle?.radio?.label, {
          error,
          disabled: isDisabled,
          checked: isChecked,
          checkedDisabled: isCheckedDisabled,
        }),
        className
      );

      const inputClass = cn(
        inputBoxStyled({ theme, error, disabled }),
        controlRadioStyled({
          theme,
          error,
          disabled: isDisabled,
          checked: isChecked,
          checkedDisabled: isCheckedDisabled,
        }),
        getCustomFormStyle(customStyle?.radio?.input, {
          error,
          disabled: isDisabled,
          checked: isChecked,
          checkedDisabled: isCheckedDisabled,
        })
      );

      return (
        <label
          key={option.value}
          htmlFor={id}
          ref={label ? undefined : ref}
          className={labelClass}
          {...createDataAttributes({
            test: `radio-${index}`,
          })}
          {...createDataGA({
            eventName: tracking?.eventName ?? undefined,
            group: tracking?.group ?? undefined,
            click: true,
            text: option?.label,
            value: option?.value,
          })}
        >
          <input
            type="radio"
            id={id}
            name={name}
            value={option.value}
            checked={isChecked}
            disabled={isDisabled}
            onChange={isDisabled ? undefined : handleChange(option, index)}
            aria-checked={isChecked}
            aria-disabled={isDisabled}
            className={inputClass}
          />
          {!!option.label && option.label}
        </label>
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, name, value, disabled, error, theme, className, customStyle]);

  const groupContent =
    options.length > 1 ? (
      <OptionsGroup
        name={name}
        orientation={orientation}
        role="radiogroup"
        className={groupClassName}
        {...createDataAttributes({ test: 'radiogroup' })}
      >
        {renderOptions}
      </OptionsGroup>
    ) : (
      renderOptions
    );

  return (
    <FieldGroup
      {...props}
      ref={label ? ref : undefined}
      inputType="control"
      label={label}
      htmlFor={name}
      theme={theme}
      customStyle={customStyle?.fieldGroup}
      {...createDataAttributes({ test: name })}
    >
      {groupContent}
    </FieldGroup>
  );
};
